import { useIntl } from 'react-intl';
import { Link, useParams } from 'react-router-dom';
import { HeroContainer } from '@fe-learner-common/components/HeroContainer';
import { InstructorsPromo } from '@fe-learner-common/components/InstructorsPromo';
import { Tabs } from '@fe-learner-common/components/Tabs';
import { TTabKeys } from '@routes/session-details/SessionDetailsRoute';
import { tabMap } from '@utils/tabMap';
import { TSelfregSessionDetails } from '@api/io/selfreg.io';
import { AuthorizationActions } from '@routes/selfreg/components/AuthorizationActions';
import { useSelfregEnrollmentActions } from '@routes/selfreg/hooks/useEnrollmentActions';
import { FaIcon, Tag } from '@fe-learner-common';
import { useSelfregSessionDetailsTags } from '@routes/selfreg/hooks/useSelfregSessionDetailsTags';
import { useApplySelfregPageConfig } from '@routes/selfreg/hooks/useApplySelfregPageConfig';
import { AutoEnrollment } from '@routes/selfreg/components/AutoEnrollment';

export const SelfRegPage = ({
  tabKeys,
  sessionDetails,
}: {
  tabKeys: TTabKeys[];
  sessionDetails: TSelfregSessionDetails;
}) => {
  useApplySelfregPageConfig();
  const intl = useIntl();
  const params = useParams();
  const selectedTab = params['*'];

  const tabs = tabKeys.map((key) => tabMap[key]);
  const enrollmentActions = useSelfregEnrollmentActions({ sessionDetails });
  const tags = useSelfregSessionDetailsTags(sessionDetails);

  return (
    <div>
      <AutoEnrollment
        sessionId={sessionDetails.id}
        courseName={sessionDetails?.name}
      />
      <HeroContainer
        courseImage={sessionDetails.thumbnailUrl}
        courseImageAlt={intl.formatMessage({
          id: 'learner.common.course.image',
        })}
        background={{
          backdrop: sessionDetails.thumbnailUrl,
          image: sessionDetails?.bgImage,
          color: sessionDetails?.bgColor,
        }}
      >
        <div>
          <h1 className="text-headline-sm">{sessionDetails?.name}</h1>
          <div className="flex flex-wrap gap-3 pt-2">
            {tags
              ? Object.entries(tags).map(([key, tag]) =>
                  tag ? (
                    <Tag
                      key={key}
                      type={tag.type}
                      className="whitespace-nowrap"
                    >
                      <FaIcon width={12} icon={tag.icon} />
                      {tag.text}
                    </Tag>
                  ) : null,
                )
              : null}
          </div>
        </div>
        <div className="flex flex-wrap gap-5">
          <AuthorizationActions sessionId={sessionDetails.id} />
          {enrollmentActions?.enrollmentAction}
          {sessionDetails.trainers?.length ? (
            <Link to="./instructors">
              <InstructorsPromo
                visiblePersons={sessionDetails.trainers
                  .slice(0, 3)
                  .map((trainer) => ({
                    name: trainer.name || '',
                    thumbnail: trainer.thumbnail || '',
                  }))}
                totalPersons={sessionDetails.trainers?.length}
              />
            </Link>
          ) : null}
        </div>
      </HeroContainer>
      <Tabs tabs={tabs} selectedTab={selectedTab} />
    </div>
  );
};
